import { useState, useEffect, useMemo } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Grid,
  IconButton,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { updatePurchaseOrder } from './functions/update_purchase_order.function';
import { notify } from '../../common/functions/react-toastify.function';
import styles from './edit_purchase_order_modal.module.css';

const getTodayDateString = () => {
  const d = new Date();
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
};

const EditPurchaseOrderModal = ({ isOpen, onClose, purchaseOrder, onUpdateSuccess }) => {
  const todayMaxDate = useMemo(() => getTodayDateString(), []);
  const [formData, setFormData] = useState({
    account: '',
    item: '',
    type: '',
    city: '',
    final_length_km: '',
    final_length_meters: '',
    unit_rate_meter: '',
    total_cost_without_pst: '',
    finalized_vendor: '',
    pr: '',
    po: '',
    po_date: '',
    request_date: '',
  });
  const [isUpdating, setIsUpdating] = useState(false);
  const [errors, setErrors] = useState({});

  // Populate form when purchase order data is available
  useEffect(() => {
    if (purchaseOrder) {
      // Format dates from table format to input format (YYYY-MM-DD)
      const formatDateForInput = (dateString) => {
        if (!dateString) {
          return '';
        }
        try {
          // If it's already in YYYY-MM-DD format, return as is
          if (dateString.match(/^\d{4}-\d{2}-\d{2}$/)) {
            return dateString;
          }
          // Otherwise parse and format
          const date = new Date(dateString);
          return date.toISOString().split('T')[0];
        } catch {
          return dateString;
        }
      };

      // Extract numeric value from unit_rate_meter if it has "PKR" suffix
      const unitRateValue = purchaseOrder.unitRateMeter
        ? String(purchaseOrder.unitRateMeter)
            .replace(/PKR\s*/gi, '')
            .trim()
        : '';

      setFormData({
        account: purchaseOrder.account || '',
        item: purchaseOrder.item || '',
        type: purchaseOrder.type || '',
        city: purchaseOrder.city || '',
        final_length_km: purchaseOrder.finalLengthKm?.toString() || '',
        final_length_meters: purchaseOrder.finalLengthMeters?.toString() || '',
        unit_rate_meter: unitRateValue,
        total_cost_without_pst: purchaseOrder.totalCostWithoutPST?.toString() || '',
        finalized_vendor: purchaseOrder.finalizedVender || '',
        pr: purchaseOrder.pr || '',
        po: purchaseOrder.po || '',
        po_date: formatDateForInput(purchaseOrder.poDate),
        request_date: formatDateForInput(purchaseOrder.requestDate),
      });
      setErrors({});
    }
  }, [purchaseOrder]);

  const handleChange = (field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    // Clear error for this field when user starts typing
    if (errors[field]) {
      setErrors((prev) => ({ ...prev, [field]: '' }));
    }
  };

  const validateForm = () => {
    const newErrors = {};

    // Add validation rules if needed
    // For now, we'll just check required fields
    if (!formData.account?.trim()) {
      newErrors.account = 'Account is required';
    }
    if (!formData.item?.trim()) {
      newErrors.item = 'Item is required';
    }
    if (!formData.po?.trim()) {
      newErrors.po = 'PO is required';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSave = async () => {
    if (!validateForm()) {
      notify('error', 'Please fill in all required fields');
      return;
    }

    if (!purchaseOrder?.id) {
      notify('error', 'Invalid purchase order data');
      return;
    }

    setIsUpdating(true);
    try {
      // Prepare payload - ensure unit_rate_meter has "PKR" suffix if it's a number
      const unitRateValue = formData.unit_rate_meter?.trim();
      const unitRateWithSuffix =
        unitRateValue && !unitRateValue.includes('PKR') ? `${unitRateValue} PKR` : unitRateValue;

      const payload = {
        account: formData.account.trim(),
        item: formData.item.trim(),
        type: formData.type.trim(),
        city: formData.city.trim(),
        final_length_km: formData.final_length_km?.trim() || '',
        final_length_meters: formData.final_length_meters?.trim() || '',
        unit_rate_meter: unitRateWithSuffix || '',
        total_cost_without_pst: formData.total_cost_without_pst?.trim() || '',
        finalized_vendor: formData.finalized_vendor.trim(),
        pr: formData.pr.trim(),
        po: formData.po.trim(),
        po_date: formData.po_date || '',
        request_date: formData.request_date || '',
      };

      const result = await updatePurchaseOrder(purchaseOrder.id, payload);

      if (result.success && result.data) {
        notify('success', result.message || 'PO record updated successfully');
        if (onUpdateSuccess) {
          onUpdateSuccess(result.data);
        }
        onClose();
      }
    } catch (error) {
      console.error('Update error:', error);
      notify('error', 'Failed to update purchase order. Please try again.');
    } finally {
      setIsUpdating(false);
    }
  };

  const handleClose = () => {
    if (!isUpdating) {
      setFormData({
        account: '',
        item: '',
        type: '',
        city: '',
        final_length_km: '',
        final_length_meters: '',
        unit_rate_meter: '',
        total_cost_without_pst: '',
        finalized_vendor: '',
        pr: '',
        po: '',
        po_date: '',
        request_date: '',
      });
      setErrors({});
      onClose();
    }
  };

  if (!purchaseOrder) {
    return null;
  }

  return (
    <Dialog open={isOpen} onClose={handleClose} maxWidth="md" fullWidth>
      <DialogTitle>
        <div className={styles['modal-header']}>
          <h2 className={styles['modal-title']}>Edit Purchase Order</h2>
          <IconButton
            onClick={handleClose}
            disabled={isUpdating}
            className={styles['close-button']}
          >
            <CloseIcon />
          </IconButton>
        </div>
      </DialogTitle>
      <DialogContent>
        <div className={styles['modal-body']}>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Account"
                value={formData.account}
                onChange={(e) => handleChange('account', e.target.value)}
                error={!!errors.account}
                helperText={errors.account}
                size="small"
                required
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Item"
                value={formData.item}
                onChange={(e) => handleChange('item', e.target.value)}
                error={!!errors.item}
                helperText={errors.item}
                size="small"
                required
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Type"
                value={formData.type}
                onChange={(e) => handleChange('type', e.target.value)}
                size="small"
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="City"
                value={formData.city}
                onChange={(e) => handleChange('city', e.target.value)}
                size="small"
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Final Length (km)"
                type="number"
                value={formData.final_length_km}
                onChange={(e) => handleChange('final_length_km', e.target.value)}
                size="small"
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Final Length (meters)"
                type="number"
                value={formData.final_length_meters}
                onChange={(e) => handleChange('final_length_meters', e.target.value)}
                size="small"
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Unit Rate (meter)"
                value={formData.unit_rate_meter}
                onChange={(e) => handleChange('unit_rate_meter', e.target.value)}
                placeholder="e.g., 500 PKR"
                size="small"
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Total Cost without PST PKR"
                type="number"
                value={formData.total_cost_without_pst}
                onChange={(e) => handleChange('total_cost_without_pst', e.target.value)}
                size="small"
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Finalized Vendor"
                value={formData.finalized_vendor}
                onChange={(e) => handleChange('finalized_vendor', e.target.value)}
                size="small"
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="PR"
                value={formData.pr}
                onChange={(e) => handleChange('pr', e.target.value)}
                size="small"
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="PO"
                value={formData.po}
                onChange={(e) => handleChange('po', e.target.value)}
                error={!!errors.po}
                helperText={errors.po}
                size="small"
                required
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="PO Date"
                type="date"
                value={formData.po_date}
                onChange={(e) => handleChange('po_date', e.target.value)}
                inputProps={{ max: todayMaxDate }}
                InputLabelProps={{ shrink: true }}
                size="small"
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Request Date"
                type="date"
                value={formData.request_date}
                onChange={(e) => handleChange('request_date', e.target.value)}
                inputProps={{ max: todayMaxDate }}
                InputLabelProps={{ shrink: true }}
                size="small"
              />
            </Grid>
          </Grid>
        </div>
      </DialogContent>
      <DialogActions className={styles['modal-footer']}>
        <Button
          onClick={handleClose}
          disabled={isUpdating}
          variant="outlined"
          sx={{
            backgroundColor: 'transparent',
            border: '1px solid #ccc',
            color: '#333',
            borderRadius: '6px',
            textTransform: 'none',
            padding: '8px 16px',
            '&:hover': {
              backgroundColor: 'rgba(0, 0, 0, 0.04)',
              border: '1px solid #ccc',
            },
            '&:disabled': {
              backgroundColor: 'transparent',
              border: '1px solid #ccc',
              color: '#999',
              opacity: 0.6,
            },
          }}
        >
          Cancel
        </Button>
        <Button
          onClick={handleSave}
          variant="contained"
          disabled={isUpdating}
          sx={{
            backgroundColor: '#bd1d23',
            color: 'white',
            borderRadius: '6px',
            textTransform: 'none',
            padding: '8px 16px',
            '&:hover': {
              backgroundColor: '#a0181e',
            },
            '&:disabled': {
              backgroundColor: '#ccc',
              color: '#666',
            },
          }}
        >
          {isUpdating ? 'Updating...' : 'Update'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default EditPurchaseOrderModal;
